// Notification sound for helpdesk events (e.g. a chat assigned to the agent).
// Muteable per-agent via localStorage so each person controls their preference.
const MUTE_KEY = "helpdesk:assignSoundMuted";

export function isAssignSoundMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === "1";
  } catch {
    return false;
  }
}

export function setAssignSoundMuted(muted: boolean): void {
  try {
    localStorage.setItem(MUTE_KEY, muted ? "1" : "0");
  } catch {
    /* ignore */
  }
}

// Um contexto só, reaproveitado: o navegador limita quantos podem existir, e
// criar um por aviso arrisca o som sumir calado depois de muitos toques.
let sharedCtx: AudioContext | null = null;

function audioContext(): AudioContext {
  if (!sharedCtx || sharedCtx.state === "closed") {
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    sharedCtx = new Ctx();
  }
  return sharedCtx;
}

/**
 * Destrava o áudio no primeiro gesto da pessoa na página.
 *
 * iOS e Chrome só deixam um AudioContext sair de "suspended" dentro de um gesto
 * do usuário. Até aqui o único lugar que fazia isso era — por acidente — o
 * botão de som no painel do sininho: quem nunca abriu aquele painel ficava sem
 * o beep de mensagem nova e não tinha como descobrir o motivo. No celular,
 * onde o beep é metade do aviso, isso é a diferença entre notar e não notar uma
 * conversa nova.
 *
 * Idempotente e silenciosa: pode ser chamada a cada toque sem efeito colateral.
 */
export function unlockAudio(): void {
  try {
    const ctx = audioContext();
    if (ctx.state === "suspended") void ctx.resume();
    // Alguns Safari só consideram o contexto realmente liberado depois de um
    // nó ter tocado dentro do gesto. Um oscilador mudo de um frame resolve.
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    g.gain.value = 0;
    o.connect(g);
    g.connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.01);
  } catch {
    /* audio not available */
  }
}

/** Short WebAudio beep — no asset needed, works offline. */
export function playNotificationBeep(): void {
  try {
    const ctx = audioContext();
    // Sem um clique anterior na página o contexto nasce suspenso.
    if (ctx.state === "suspended") void ctx.resume();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g);
    g.connect(ctx.destination);
    o.frequency.value = 660;
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
    o.start();
    o.stop(ctx.currentTime + 0.26);
  } catch {
    /* audio not available */
  }
}
